import { type ReactNode } from 'react';
import {
  BookOpen,
  Database,
  Home,
  Layers,
  Newspaper,
  Pill,
  Mic2,
  MapPin,
  Settings,
  Star,
  type LucideIcon,
} from 'lucide-react';

// Guía de uso del panel: acciones, lógica y reglas de negocio de cada
// apartado, más una sección dedicada a la gestión de la caché. El
// contenido es estático (es la documentación del propio panel); se
// mantiene a mano cuando se crea o modifica una funcionalidad.

interface DocSection {
  id: string;
  title: string;
  icon: LucideIcon;
  body: ReactNode;
}

// — Helpers de presentación (mantienen el contenido legible y consistente) —

function Lead({ children }: { children: ReactNode }) {
  return <p className="text-sm leading-relaxed text-muted-foreground">{children}</p>;
}

function SubTitle({ children }: { children: ReactNode }) {
  return (
    <h3 className="mt-6 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </h3>
  );
}

function P({ children }: { children: ReactNode }) {
  return <p className="text-sm leading-relaxed">{children}</p>;
}

function List({ children }: { children: ReactNode }) {
  return <ul className="ml-5 list-disc space-y-1.5 text-sm leading-relaxed">{children}</ul>;
}

function Term({ children }: { children: ReactNode }) {
  return <span className="font-medium text-foreground">{children}</span>;
}

function Note({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-md border border-amber-300/60 bg-amber-50 p-3 text-sm leading-relaxed text-amber-900">
      {children}
    </div>
  );
}

const SECTIONS: DocSection[] = [
  {
    id: 'introduccion',
    title: 'Introducción',
    icon: BookOpen,
    body: (
      <div className="space-y-3">
        <Lead>
          Este panel administra el contenido propio de Baila.fm: lo que se
          destaca, se ordena y se publica en la web pública. Los eventos,
          artistas y recintos no se crean aquí — viven en el Core (Baila Pro) y
          el panel sólo los selecciona y enriquece.
        </Lead>
        <SubTitle>Qué puedes gestionar y qué no</SubTitle>
        <List>
          <li>
            <Term>Sí:</Term> destacados de la home, colecciones, blog, chips del
            buscador, artistas y ciudades destacadas, y la purga de caché.
          </li>
          <li>
            <Term>No:</Term> el catálogo de eventos, sus precios, fechas o
            disponibilidad. Eso es fuente de verdad del Core; el panel lo
            consume en modo lectura.
          </li>
        </List>
        <SubTitle>Acceso</SubTitle>
        <P>
          El panel está protegido por inicio de sesión con enlace mágico al
          correo. Cualquier persona con sesión válida puede operar todo el
          panel: no hay roles ni permisos por sección.
        </P>
      </div>
    ),
  },
  {
    id: 'conceptos',
    title: 'Conceptos transversales',
    icon: Database,
    body: (
      <div className="space-y-3">
        <Lead>
          Reglas que se repiten en varios apartados. Conviene tenerlas claras
          antes de entrar en cada gestor.
        </Lead>

        <SubTitle>Contenido en tres idiomas</SubTitle>
        <P>
          Los textos que acaban en la web (títulos, descripciones, etc.) se
          editan en <Term>español, catalán e inglés</Term>. Salvo que se indique
          lo contrario, el <Term>español es obligatorio</Term> y catalán e
          inglés son opcionales.
        </P>
        <List>
          <li>
            La web <Term>no hace cascada de idioma</Term> a nivel de entidad: si
            una colección o entrada de blog no tiene título en el idioma que
            pide el visitante, esa página no se muestra en ese idioma (devuelve
            404). Por eso, para que algo aparezca en catalán o inglés, hay que
            traducirlo.
          </li>
          <li>
            La marca «Baila» <Term>nunca se traduce</Term>: en cualquier idioma
            el copy dice literalmente «Baila».
          </li>
        </List>

        <SubTitle>Slugs (la parte de la URL)</SubTitle>
        <List>
          <li>
            El slug se <Term>autogenera a partir del título en español</Term> al
            crear el elemento.
          </li>
          <li>
            Sólo es editable <Term>al crear</Term>. Una vez guardado es{' '}
            <Term>inmutable</Term>: no se puede cambiar (cambiarlo rompería los
            enlaces ya publicados y el SEO).
          </li>
          <li>Debe ser único dentro de su tipo (dos colecciones no pueden compartir slug).</li>
        </List>

        <SubTitle>Guardado y ediciones simultáneas</SubTitle>
        <P>
          Algunos gestores avisan si <Term>otra persona modificó el mismo
          elemento</Term> mientras lo editabas (destacados, artistas y
          ciudades). En ese caso el guardado falla con un aviso para que
          recargues y no pises sus cambios. Los chips, las colecciones y el
          blog, en cambio, funcionan en modo «el último guardado manda»
          porque sus side-channels (reordenado y subida de imágenes) tocan
          el documento por su cuenta y exigir control de concurrencia
          generaba avisos falsos al guardar.
        </P>

        <SubTitle>Imágenes</SubTitle>
        <List>
          <li>
            Al subir una imagen recortas el encuadre (crop) con la proporción
            que pide cada sitio. El panel la <Term>optimiza y convierte a
            WebP</Term> automáticamente.
          </li>
          <li>
            La subida se aplica <Term>de inmediato</Term> (no espera al «Guardar»
            del formulario).
          </li>
        </List>

        <SubTitle>Avisos</SubTitle>
        <P>
          Toda acción de guardado muestra un aviso de éxito o de error. El botón
          se deshabilita mientras la petición está en curso para evitar envíos
          duplicados.
        </P>
      </div>
    ),
  },
  {
    id: 'home',
    title: 'Home (panel)',
    icon: Home,
    body: (
      <div className="space-y-3">
        <Lead>
          Pantalla de inicio del panel. Hoy es un resumen; el trabajo real
          ocurre en las secciones del menú.
        </Lead>
        <Note>
          No confundir esta «Home» (la del panel) con la home de la web pública,
          que se compone desde <Term>Destacados</Term>, <Term>Artistas
          destacados</Term> y <Term>Ciudades destacadas</Term>.
        </Note>
      </div>
    ),
  },
  {
    id: 'destacados',
    title: 'Destacados',
    icon: Star,
    body: (
      <div className="space-y-3">
        <Lead>
          Gestiona los bloques de colección que componen la home de la web. Está
          compuesta de 5 tipos de bloque posibles; se pueden añadir varios,
          reordenar por drag&amp;drop, activar/desactivar y quitar.
          Artistas destacados y Ciudades son bloques fijos (siempre presentes,
          no se pueden añadir ni quitar; solo activar/desactivar).
        </Lead>
        <SubTitle>Tipos de bloque</SubTitle>
        <List>
          <li>
            <Term>Hero destacado</Term> — la colección protagonista de la parte
            superior (carrusel grande).
          </li>
          <li>
            <Term>Tarjetas</Term> — carrusel horizontal de tarjetas con eventos
            de una colección. Puede haber varios bloques de este tipo.
          </li>
          <li>
            <Term>Colección destacada</Term> — bloque panorámico con título,
            fechas y CTA. Puede haber varios.
          </li>
          <li>
            <Term>Artistas destacados</Term> — carrusel de artistas. Solo
            controlas su posición; el contenido se edita desde la página de
            <Term>Artistas destacados</Term>.
          </li>
          <li>
            <Term>Elige tu ciudad</Term> — marquee de ciudades. Solo controlas
            su posición; el contenido se edita desde <Term>Ciudades destacadas</Term>.
          </li>
        </List>
        <SubTitle>Reglas</SubTitle>
        <List>
          <li>
            <Term>Artistas destacados</Term> y <Term>Elige tu ciudad</Term> son{' '}
            bloques fijos: siempre presentes en la home, no se pueden añadir
            ni quitar, solo activar/desactivar. El resto de tipos pueden repetirse.
          </li>
          <li>
            Sólo se pueden asignar colecciones <Term>completas en los tres
            idiomas</Term> (es + ca + en). Las incompletas aparecen
            deshabilitadas con el aviso de qué idioma falta — el sistema
            rechazaría su asignación.
          </li>
          <li>
            El selector de colección tiene <Term>buscador</Term>: escribe parte
            del título para filtrar. Busca por el título en cualquiera de los
            tres idiomas y por el slug, así que encuentra la colección aunque la
            conozcas por su nombre en otro idioma.
          </li>
          <li>
            Si otra persona cambió el layout mientras editabas, el guardado avisa
            para que recargues.
          </li>
        </List>
        <P>
          Para preparar una colección para la home, complétale las traducciones
          en <Term>Colecciones</Term> y luego asígnala aquí.
        </P>
      </div>
    ),
  },
  {
    id: 'colecciones',
    title: 'Colecciones',
    icon: Layers,
    body: (
      <div className="space-y-3">
        <Lead>
          Una colección es un grupo de eventos con una temática común (un
          artista, un estilo, una campaña…). Tiene su propia página pública y se
          usa como material para los huecos destacados de la home.
        </Lead>
        <SubTitle>Campos</SubTitle>
        <List>
          <li>
            <Term>Título</Term> (trilingüe; ES obligatorio, ca/en opcionales).
          </li>
          <li>
            <Term>Slug</Term>: autogenerado del título ES, editable sólo al
            crear, inmutable después. El botón junto al campo copia la URL
            pública de la colección (ES por defecto; el desplegable permite
            elegir catalán o inglés si la colección tiene título en esos
            idiomas).
          </li>
          <li>
            <Term>Descripción</Term> (trilingüe, opcional).
          </li>
          <li>
            <Term>Imágenes</Term>: imagen de página (vertical, 3:4) y miniatura
            (apaisada, ~2:1).
          </li>
          <li>
            <Term>Eventos</Term>: se buscan y se añaden desde el buscador del
            formulario, y se <Term>reordenan arrastrando</Term>.
          </li>
        </List>
        <SubTitle>Datos calculados (no se editan)</SubTitle>
        <P>
          El <Term>precio «desde»</Term> y el <Term>rango de fechas</Term> no se
          escriben a mano: se calculan al leer la colección, cruzando sus
          eventos contra el Core. Si el Core no responde, la colección se muestra
          igualmente, sin esos agregados.
        </P>
        <SubTitle>Reglas</SubTitle>
        <List>
          <li>El slug debe ser único; al crear se valida.</li>
          <li>
            Una colección <Term>asignada a la home</Term> no se puede dejar con
            el título incompleto: debe mantener los tres idiomas. El sistema lo
            impide al guardar.
          </li>
          <li>
            El botón <Term>Copiar URL pública</Term> en cada fila es una
            pastilla con dos zonas: el icono de enlace copia la URL en{' '}
            <Term>ES</Term> (con fallback al primer idioma con título si no
            hay ES), y el chevron despliega el resto de idiomas disponibles
            para copiar la URL en otra lengua. Un idioma sin título haría 404
            en la web, así que el dropdown solo muestra los que tienen título.
          </li>
          <li>Funciona en modo «el último guardado manda» (por las subidas de imagen).</li>
          <li>
            Si hay <Term>cambios sin guardar</Term>, cerrar el formulario (la
            «X», «Cancelar», la tecla Esc o un clic fuera) pide confirmación
            antes de descartarlos. Al guardar con éxito, el formulario se
            cierra directamente sin ese aviso.
          </li>
        </List>
        <SubTitle>Paginación del listado</SubTitle>
        <P>
          El listado de colecciones está paginado (20 colecciones por página). Los
          controles de navegación aparecen al pie de la tabla cuando hay más de una
          página. El endpoint <code>/admin/collections/all</code> (usado internamente por
          la página Destacados) devuelve el listado completo sin paginar. Al crear
          o editar una colección, el listado se refresca automáticamente en la
          página actual.
        </P>
      </div>
    ),
  },
  {
    id: 'blog',
    title: 'Blog',
    icon: Newspaper,
    body: (
      <div className="space-y-3">
        <Lead>
          Gestor de entradas del blog (hoy, noticias). Cada entrada es un único
          documento con sus tres idiomas dentro y un cuerpo por bloques al estilo
          de un editor tipo WordPress.
        </Lead>
        <SubTitle>Anatomía de una entrada</SubTitle>
        <List>
          <li>
            <Term>Título y subtítulo</Term> (trilingües).
          </li>
          <li>
            <Term>Extracto</Term> (resumen ~160 caracteres; alimenta las
            tarjetas y la descripción para buscadores).
          </li>
          <li>
            <Term>Título SEO</Term> (opcional; sobrescribe el título de la
            pestaña/buscador).
          </li>
          <li>
            <Term>Cuerpo por bloques</Term>: cabeceras, textos (editor con
            negrita/cursiva/enlaces), imágenes, colección, artista, evento,
            CTA (botón enlazable) y embeds de YouTube, Spotify, X y TikTok;
            se reordenan y se borran.
          </li>
          <li>
            <Term>Imágenes</Term>: destacada (2.4:1) y miniatura (16:9). Son
            únicas por entrada (no por idioma).
          </li>
        </List>
        <SubTitle>Tipos de bloque</SubTitle>
        <List>
          <li>
            <Term>Colección</Term>, <Term>Artista</Term> y <Term>Evento</Term>{' '}
            solo guardan el <em>slug</em> elegido; el título, la imagen y el
            resto de datos se resuelven en el momento de servir el artículo
            (no se copian). Si la entidad referenciada se borra o cambia de
            slug, la API la <em>omite en silencio</em> del artículo público —
            no da error, simplemente ese bloque deja de aparecer. Si un
            bloque "desaparece" de la web sin que nadie lo haya borrado
            desde aquí, sospecha primero de esto.
          </li>
          <li>
            <Term>CTA</Term>: texto del botón (trilingüe) + URL de destino +
            casilla "abrir en nueva pestaña". Sin resolución server-side, se
            guarda y sirve tal cual.
          </li>
          <li>
            <Term>Imagen</Term>: la imagen del cuerpo, su texto alternativo
            (alt) y un pie, ambos trilingües. Opcionalmente admite un{' '}
            <Term>enlace</Term>: al rellenarlo, en la web la imagen se
            vuelve clicable hacia esa URL (con casilla "abrir en nueva
            pestaña"). El enlace acepta ruta interna (<code>/…</code>) o{' '}
            <code>http(s)://</code>; el <em>alt</em> y el pie son por idioma,
            pero el enlace es único (no cambia entre idiomas).
          </li>
          <li>
            <Term>YouTube</Term> y <Term>Spotify</Term>: se pega la URL tal
            cual (cualquier formato — <code>watch?v=</code>,{' '}
            <code>youtu.be/</code>, enlace compartido de Spotify, etc.) y el
            front la normaliza a embed. No requieren API key. YouTube usa{' '}
            <code>youtube-nocookie.com</code> (modo sin cookies hasta que el
            visitante interactúa con el reproductor).
          </li>
          <li>
            Los embeds de <Term>X</Term> y <Term>TikTok</Term> pegan la URL
            de la publicación/vídeo tal cual (
            <code>x.com/usuario/status/ID</code> o{' '}
            <code>tiktok.com/@usuario/video/ID</code>); no requieren API key.
            En la web, si CookieYes ya tiene aceptada la categoría de
            embeds de terceros (configurable por env, por defecto
            "advertisement"), cargan directo; si no, se muestra un
            placeholder y solo cargan al hacer click — nunca fijan
            cookies de terceros sin consentimiento.
          </li>
        </List>
        <SubTitle>Estados y publicación</SubTitle>
        <List>
          <li>
            Una entrada es <Term>borrador</Term> o <Term>publicada</Term>. Sólo
            las publicadas se ven en la web.
          </li>
          <li>
            La <Term>fecha de publicación</Term> se fija al publicar por primera
            vez y luego se puede editar a mano. El listado público ordena por esa
            fecha (la más reciente es el hero).
          </li>
          <li>
            <Term>Previsualizar</Term> abre la web con la entrada (también en
            borrador) mediante un enlace temporal.
          </li>
        </List>
        <SubTitle>Reglas</SubTitle>
        <List>
          <li>El slug se autogenera del título ES, es único e inmutable.</li>
          <li>
            La web no hace cascada a nivel de entrada: una entrada sin título en
            un idioma no aparece en ese idioma.
          </li>
          <li>Funciona en modo «el último guardado manda».</li>
        </List>
        <SubTitle>Paginación del listado</SubTitle>
        <P>
          El listado de entradas del blog está paginado (20 entradas por página).
          Los controles de navegación aparecen al pie de la tabla cuando hay más de
          una página. Al crear, editar o eliminar una entrada, el listado se
          refresca automáticamente en la página actual. Si al eliminar una entrada
          la página se queda vacía, se retrocede a la página anterior.
        </P>
      </div>
    ),
  },
  {
    id: 'chips',
    title: 'Chips del hero',
    icon: Pill,
    body: (
      <div className="space-y-3">
        <Lead>
          Los chips son los accesos rápidos del buscador del hero de la home
          (atajos a búsquedas o páginas). Se muestran en el orden que definas.
        </Lead>
        <SubTitle>Campos y reglas</SubTitle>
        <List>
          <li>
            <Term>Etiqueta</Term> trilingüe. Aquí basta con{' '}
            <Term>al menos un idioma</Term> (no hay obligatorio fijo ni cascada
            por defecto): se muestra el idioma del visitante si existe.
          </li>
          <li>
            <Term>Destino</Term> (la URL o búsqueda a la que lleva el chip).
          </li>
        </List>
        <SubTitle>Activos y ocultos</SubTitle>
        <List>
          <li>
            Los chips <Term>activos</Term> se ordenan arrastrando; ese orden es
            el que ve el usuario.
          </li>
          <li>
            Los <Term>ocultos</Term> no aparecen en la web pero se conservan para
            reactivarlos.
          </li>
          <li>
            Activar/ocultar y reordenar se aplican al momento; si la API rechaza,
            el cambio se revierte. El guardado avisa si otra persona los modificó.
          </li>
        </List>
      </div>
    ),
  },
  {
    id: 'artistas-destacados',
    title: 'Artistas destacados',
    icon: Mic2,
    body: (
      <div className="space-y-3">
        <Lead>
          Lista ordenada de artistas que aparecen destacados en la home. Es una
          única lista (no hay activos/ocultos).
        </Lead>
        <List>
          <li>
            Los artistas se <Term>buscan desde el Core</Term> (no se crean aquí):
            el buscador trae los artistas de la organización.
          </li>
          <li>Se añaden, se reordenan arrastrando y se eliminan de la lista.</li>
          <li>El guardado avisa si otra persona modificó la lista mientras editabas.</li>
        </List>
      </div>
    ),
  },
  {
    id: 'ciudades-destacadas',
    title: 'Ciudades destacadas',
    icon: MapPin,
    body: (
      <div className="space-y-3">
        <Lead>
          Lista ordenada de ciudades del bloque «Elige tu ciudad» de la home.
          Una ciudad es sólo su nombre.
        </Lead>
        <List>
          <li>
            El selector ofrece las <Term>ciudades disponibles</Term> según los
            eventos del catálogo (se filtran escribiendo).
          </li>
          <li>Se añaden, se reordenan arrastrando y se eliminan.</li>
          <li>El guardado avisa si otra persona modificó la lista.</li>
        </List>
      </div>
    ),
  },
  {
    id: 'configuracion',
    title: 'Configuración',
    icon: Settings,
    body: (
      <div className="space-y-3">
        <Lead>
          Herramientas de mantenimiento. Hoy: la purga manual de la caché de la
          web. Ver la sección{' '}
          <a href="#cache" className="font-medium text-foreground underline">
            Gestión de la caché
          </a>{' '}
          para entender cuándo y por qué usarla.
        </Lead>
        <List>
          <li>
            <Term>Vaciar toda la caché</Term> — fuerza el refresco de todo el
            contenido publicado.
          </li>
          <li>
            <Term>Por sección</Term> — vacía sólo una superficie (eventos,
            colecciones, home, blog, chips, artistas, ciudades) para no
            recalentar todo el sitio cuando sólo cambió una cosa.
          </li>
        </List>
      </div>
    ),
  },
  {
    id: 'cache',
    title: 'Gestión de la caché',
    icon: Database,
    body: (
      <div className="space-y-3">
        <Lead>
          La web cachea el contenido para ir rápida y consultar menos al Core. El
          objetivo es: caché con vida larga, pero <Term>frescura
          inmediata</Term> cuando publicas algo desde el panel.
        </Lead>

        <SubTitle>Lo que tienes que recordar</SubTitle>
        <List>
          <li>
            Cuando <Term>guardas un cambio en el panel</Term> (blog, colecciones,
            destacados, chips, artistas, ciudades), la web se refresca{' '}
            <Term>automáticamente</Term> en esa zona. No necesitas hacer nada
            más.
          </li>
          <li>
            La purga manual de <Term>Configuración</Term> es una red de
            seguridad: úsala si algo no se actualizó como esperabas, o tras un
            cambio urgente que quieras ver reflejado ya.
          </li>
        </List>

        <SubTitle>Cómo funciona por dentro</SubTitle>
        <P>
          La caché vive <Term>al lado de la web</Term> (no en la API). Cada zona
          de contenido lleva una «etiqueta». Cuando guardas en el panel, la API
          avisa a la web de qué etiquetas refrescar; la web vuelve a pedir esos
          datos en la siguiente visita. Es un mecanismo best-effort: si la
          notificación fallara, el contenido se autocura igualmente al expirar su
          tiempo de vida.
        </P>

        <SubTitle>Frescura por tipo de contenido</SubTitle>
        <List>
          <li>
            <Term>Contenido del panel</Term> (blog, colecciones —parte editorial—,
            destacados, chips, artistas, ciudades): se refresca{' '}
            <Term>al instante</Term> al guardar.
          </li>
          <li>
            <Term>Datos de evento dentro de una colección</Term> (precio «desde»,
            fechas): pueden tardar <Term>unos minutos</Term> en reflejarse, porque
            se refrescan por su tiempo de vida y no por aviso directo. Es contenido
            de marketing y el desfase es bajo riesgo.
          </li>
          <li>
            <Term>Catálogo de eventos</Term> (listado, ficha de detalle, filtros):
            es dueño del Core, no del panel. Hoy se refresca por su tiempo de vida
            corto (~1 min). La invalidación instantánea está preparada pero{' '}
            <Term>inactiva</Term> hasta que el Core avise de los cambios de evento.
          </li>
        </List>

        <Note>
          Resumen: lo que tú publicas desde el panel aparece enseguida. Los
          precios y fechas que vienen del Core pueden tardar unos minutos. Si
          tienes prisa, usa la purga manual en <Term>Configuración</Term>.
        </Note>
      </div>
    ),
  },
];

export default function DocumentacionPage() {
  return (
    <div className="mx-auto flex max-w-5xl gap-10">
      <div className="min-w-0 flex-1 space-y-10">
        <header className="space-y-1">
          <h1 className="text-3xl font-semibold tracking-tight">Documentación</h1>
          <p className="text-muted-foreground">
            Guía de uso del panel: qué hace cada apartado, su lógica y las reglas
            de negocio. Incluye la gestión de la caché.
          </p>
        </header>

        {SECTIONS.map(({ id, title, icon: Icon, body }) => (
          <section key={id} id={id} className="scroll-mt-8 space-y-3">
            <h2 className="flex items-center gap-2 border-b pb-2 text-xl font-semibold">
              <Icon className="size-5 text-muted-foreground" aria-hidden />
              {title}
            </h2>
            {body}
          </section>
        ))}
      </div>

      <nav
        aria-label="Índice"
        className="sticky top-8 hidden h-fit w-52 shrink-0 lg:block"
      >
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          En esta página
        </p>
        <ul className="space-y-1 border-l">
          {SECTIONS.map(({ id, title }) => (
            <li key={id}>
              <a
                href={`#${id}`}
                className="-ml-px block border-l border-transparent py-1 pl-3 text-sm text-muted-foreground hover:border-foreground hover:text-foreground"
              >
                {title}
              </a>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
